import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, useOutletContext } from 'react-router-dom'
import { Scrollama, Step } from 'react-scrollama'
import ReactPlayer from 'react-player'
import { getScrollerById } from '../content'
import SiteFooter from './SiteFooter'
import SiteMark from './SiteMark'

// The external links that used to sit at the foot of this page have moved to
// /climate-resilience, where they have room and are not buried under a long scroll.
const learnMoreContent = (
  <div className='learn-more-container learn-more-outro'>
    <div className='content-container'>
      <h2>Climate resilience</h2>
      <p className='learn-more-outro-text'>
        What is happening to the glaciers, and to the people who live beneath them.
      </p>
      <p className='learn-more-outro-link'>
        <a className='link-button' href='/climate-resilience'>Read more</a>
      </p>
    </div>
  </div>
)

const parseStepContent = (step) => {
  const content = step.Content
  const parsed = content.map((item) => {
    switch (item.type) {
      case 'heading':
        const HeadingTag = `h${item.level}`;
        if (item.children[0].text !== '') {
          return <HeadingTag key={item.children[0].text}>{item.children[0].text}</HeadingTag>;
        }
        return null;
      case 'paragraph':
        if (item.children[0].code === true) {
          return <div key={item.children[0].text} dangerouslySetInnerHTML={{ __html: item.children[0].text }} />
        }
        else if (item.children[0].text !== '') {
          return <p key={item.children[0].text}>{item.children[0].text}</p>;
        }
        return null;
      default:
        return null;
    }
  }).filter(item => item !== null)
  if (parsed.length === 0) {
    return null
  } else {
    return (
      <div className={`step-content ${step.DarkTextBackdrop ? 'with-backdrop' : ''}`}>
      {parsed}
      </div>
    )
  }
};

const Scroller = () => {
  const dispatch = useDispatch()
  const { scrollerId } = useParams()
  const { scroller, scrollers } = useSelector((state) => state.content)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [steps, setSteps] = useState([])
  const [fixedContentId, setFixedContentId] = useState(null)
  const { isMobile, isLandscape } = useOutletContext()
  const [progress, setProgress] = useState(0)
  const scrollerContainerRef = useRef(null)
  const [isScrollamaComplete, setIsScrollamaComplete] = useState(false)

  const onStepEnter = ({ data }) => {
    setCurrentStepIndex(data)
    const currentStep = steps.find(s => s.StepID === data)
    if (currentStep && currentStep.ContentStepOverride) {
      setFixedContentId(currentStep.ContentStepOverride)
    } else {
      setFixedContentId(null)
    }
  }

  const onStepProgress = ({ progress }) => {
    setProgress(progress)
  }

  useEffect(() => {
    if (progress >= 1 && currentStepIndex >= steps.length) {
      setIsScrollamaComplete(true)
    } else {
      setIsScrollamaComplete(false)
    }
  }, [progress, currentStepIndex, steps.length])

  useEffect(() => {
    if (isScrollamaComplete) {
      document.body.style.overflow = 'auto'
      if (scrollerContainerRef.current) {
        scrollerContainerRef.current.style.height = 'auto'
      }
    }
  }, [isScrollamaComplete])

  useEffect(() => {
    if (scrollerId) {
      const scroller = scrollers.find((scroller) => scroller.URL === scrollerId)
      if (scroller && scroller.documentId) {
        dispatch(getScrollerById(scroller.documentId))
      }
    }
  }, [scrollerId, dispatch, scrollers])

  useEffect(() => {
    if (scroller) {
      setSteps(scroller?.Step || [])
    }
  }, [scroller])

  const isCurrentStep = (step) => {
    const currentStep = steps.find(s => s.StepID === currentStepIndex)
    if (currentStep && currentStep.VideoStepOverride) {
      return step.StepID === currentStep.VideoStepOverride
    }
    return currentStepIndex === step.StepID
  }

  const isFirstStep = (step) => steps.length > 0 && step.StepID === steps[0].StepID

  const renderStepContent = (step) => (
    <div className={`step-content-container ${step.ContentAlignment ? `content-${step.ContentAlignment}` : ''}`}>
      {/* Only the first step carries the h1. Every step had one, which gave
          the page eleven, and a search engine reads the h1 as the subject of
          the page. */}
      {step.Title && (isFirstStep(step)
        ? <h1>{step.Title}</h1>
        : <h2>{step.Title}</h2>)}
      {step.Subtitle && (isFirstStep(step)
        ? <h2>{step.Subtitle}</h2>
        : <h3>{step.Subtitle}</h3>)}
      {step.Content && parseStepContent(step)}
    </div>
  )

  return (
    <>
    <SiteMark />
    <div className='scroller-container' ref={scrollerContainerRef} style={{ pointerEvents: isScrollamaComplete ? 'none' : 'auto' }}>
      <Scrollama 
        onStepEnter={onStepEnter} 
        onStepProgress={onStepProgress} 
        offset={0.5}
        style={{ 
          position: isScrollamaComplete ? 'relative' : 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
        }}
      >
        <div style={{ position: 'fixed', top: 0, right: 0, zIndex: 10, display: 'none' }}>Step Index: {currentStepIndex}</div>
        <div
          className='scroller-video-container'
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%', 
            zIndex: 1,
            overflow: 'hidden'
        }}>
          {steps.map((step, index) => (
            <div
              key={step.StepID}
              className={`step-id-${step.stepID} step-index-${index + 1}`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: isCurrentStep(step) && !isScrollamaComplete ? 1 : 0,
                transition: 'opacity 1s ease-in-out',
              }}
            >
              {step.VideoURL || step.PortraitVideoURL ? (
                <ReactPlayer
                  url={isMobile && !isLandscape && step.PortraitVideoURL ? step.PortraitVideoURL : step.VideoURL}
                  width='100%'
                  height='100%'
                  playing={isCurrentStep(step)}
                  loop
                  muted
                  playsinline
                  config={{
                    file: {
                      attributes: {
                        preload: 'auto',
                        autoPlay: true,
                      },
                    },
                  }}
                />
              ) : (step.ImageURL || step.PortraitImageURL) ? (
                <div style={{
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${isMobile && !isLandscape && step.PortraitImageURL ? step.PortraitImageURL : step.ImageURL})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }} />
              ) : null}
            </div>
          ))}
        </div>
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          {steps.map((step, index) => (
            <div 
              key={step.StepID}
              className={`step-id-${step.stepID} step-index-${index + 1} fixed-content`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: fixedContentId === step.StepID && !isScrollamaComplete ? 1 : 0,
                transition: 'opacity 0.5s ease-in-out',
              }}
            >
              {renderStepContent(step)}
            </div>
          ))}
        </div>
        {steps.map((step, index) => (
          <Step data={step.StepID} key={step.StepID}>
            <div
              className={`step-id-${step.stepID} step-index-${index + 1}`}
              style={{ 
              minHeight: '100vh', 
              // display: 'flex', 
              // alignItems: 'center', 
              // justifyContent: 'center',
            }}>
              <div style={{
                opacity: (!fixedContentId && currentStepIndex === step.StepID && !isScrollamaComplete) ? 1 : 0,
                // transition: 'opacity 0.5s ease-in-out',
              }}>
                {renderStepContent(step)}
              </div>
            </div>
          </Step>
        ))}
      </Scrollama>
    </div>
      <div
        id="learn-more"
        className='scroller-outro'
        style={{
          marginTop: isScrollamaComplete ? 0 : '100vh',
          // the container above turns pointer events off once the scroller
          // finishes, and covers the page with a fixed video layer until it
          // does; neither should reach the links down here
          pointerEvents: 'auto',
          position: 'relative',
          zIndex: 20,
        }}
      >
        {learnMoreContent}
        <SiteFooter />
      </div>
    </>
  )
}

export default Scroller